/**
 * DocumentDetailPage — metadata, version history, verify + download for a
 * single document (TASK-03).
 */

import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, ShieldCheck, FileText, Clock } from 'lucide-react'
import { getDocumentService } from '@/services/documents'
import { toast } from '@/store/toastStore'
import type { Document, DocumentVersion } from '@/types/document.types'
import {
  DOCUMENT_STATUS_LABEL,
  labelForCategory,
  formatFileSize,
} from '@/types/document.types'
import { useAuth } from '@/contexts/AuthContext'

function formatDate(value?: string): string {
  if (!value) return '—'
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

export default function DocumentDetailPage() {
  const { hasPermission } = useAuth()
  const canDownload = hasPermission('txh.document.download')
  const canVerify = hasPermission('txh.document.verify')
  const canReadVersions = hasPermission('txh.document.version.read')
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const [doc, setDoc] = useState<Document | null>(null)
  const [versions, setVersions] = useState<DocumentVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!documentId) return
    setLoading(true)
    setError('')
    try {
      const service = getDocumentService()
      const [d, v] = await Promise.all([
        service.get(documentId),
        canReadVersions ? service.versions(documentId).catch(() => []) : Promise.resolve([]),
      ])
      setDoc(d)
      setVersions(v)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document')
    } finally {
      setLoading(false)
    }
  }, [canReadVersions, documentId])

  useEffect(() => {
    load()
  }, [load])

  const handleDownload = async () => {
    if (!canDownload || !doc) return
    try {
      const url = await getDocumentService().getDownloadUrl(doc.id)
      if (url) window.open(url, '_blank', 'noopener')
      else toast.success('Download started', doc.name)
    } catch (err) {
      toast.error('Download failed', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  const handleVerify = async () => {
    if (!canVerify || !doc) return
    try {
      const patch = await getDocumentService().verify(doc.id, true)
      setDoc({ ...doc, ...patch })
      toast.success('Document verified', doc.name)
    } catch (err) {
      toast.error('Verify failed', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  if (loading) {
    return <div className="card card-elevated p-12 text-center text-text-secondary">Loading document…</div>
  }

  if (error || !doc) {
    return (
      <div className="card card-elevated p-12 text-center">
        <p className="text-text-secondary">{error || 'Document not found'}</p>
        <button onClick={() => navigate('/documents')} className="btn btn-primary btn-sm mt-4">
          Back to Documents
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <button onClick={() => navigate('/documents')} className="btn btn-ghost btn-sm flex items-center gap-2 -ml-2">
        <ArrowLeft className="w-4 h-4" />
        Back to Documents
      </button>

      <div className="card card-elevated p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
              <FileText className="w-7 h-7 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">{doc.name}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-text-secondary flex-wrap">
                <span>{labelForCategory(doc.category)}</span>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-surface-overlay">
                  {DOCUMENT_STATUS_LABEL[doc.status] ?? doc.status}
                </span>
                <span>{formatFileSize(doc.fileSize)}</span>
                {doc.isVerified && (
                  <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4" /> Verified
                  </span>
                )}
              </div>
              {doc.description && <p className="text-text-secondary mt-3">{doc.description}</p>}
            </div>
          </div>
          {(canDownload || (canVerify && !doc.isVerified)) && (
            <div className="flex items-center gap-2">
              {canDownload && (
                <button onClick={handleDownload} className="btn btn-outline btn-sm gap-2">
                  <Download className="w-4 h-4" /> Download
                </button>
              )}
              {canVerify && !doc.isVerified && (
              <button onClick={handleVerify} className="btn btn-primary btn-sm gap-2">
                <ShieldCheck className="w-4 h-4" /> Verify
              </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-surface-overlay text-sm">
          <div>
            <p className="text-text-tertiary">Uploaded</p>
            <p className="text-text-primary font-medium">{formatDate(doc.createdAt)}</p>
          </div>
          <div>
            <p className="text-text-tertiary">Uploaded by</p>
            <p className="text-text-primary font-medium">{doc.createdByName ?? '—'}</p>
          </div>
          <div>
            <p className="text-text-tertiary">Version</p>
            <p className="text-text-primary font-medium">v{doc.version}</p>
          </div>
          <div>
            <p className="text-text-tertiary">Verified at</p>
            <p className="text-text-primary font-medium">{formatDate(doc.verifiedAt)}</p>
          </div>
        </div>
      </div>

      {/* Versions */}
      {canReadVersions && (
        <div className="card card-elevated p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-text-tertiary" /> Version history
        </h2>
        {versions.length === 0 ? (
          <p className="text-text-secondary text-sm">No prior versions.</p>
        ) : (
          <ul className="space-y-2">
            {versions.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between p-3 rounded-lg bg-surface-overlay/40 text-sm"
              >
                <span className="text-text-primary font-medium">
                  v{v.version} {v.isLatest && <span className="text-primary-600">(latest)</span>}
                </span>
                <span className="text-text-secondary">{formatFileSize(v.fileSize)}</span>
                <span className="text-text-secondary">{formatDate(v.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
        </div>
      )}
    </div>
  )
}
